import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  IdNameDto,
  MembershipPackageListRow,
  ParkingFeeGroupTableDto,
  ParkingFeeTableDto,
  ParkingRateOption,
  ParkingSubscriptionTableDto,
  SaveParkingFeeGroupPayload,
  SaveParkingFeePayload,
  SaveParkingSubscriptionPayload,
} from '../../../models/finance/master-fee.model';

@Injectable({ providedIn: 'root' })
export class ApiMasterFeeService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  getParkingFeeForTable(page = 1, pageSize = 20, searchText?: string | null): Observable<ParkingFeeTableDto> {
    return this.getTable('/Backoffice/getParkingFeeForTable', page, pageSize, searchText);
  }

  getParkingFeeGroupForTable(page = 1, pageSize = 20, searchText?: string | null): Observable<ParkingFeeGroupTableDto> {
    return this.getTable('/Backoffice/getParkingFeeGroupForTable', page, pageSize, searchText);
  }

  getParkingSubscriptionForTable(
    siteId: number,
    page = 1,
    pageSize = 20,
    searchText?: string | null
  ): Observable<ParkingSubscriptionTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId, page, pageSize };
    if (searchText?.trim()) params['searchText'] = searchText.trim();

    return this.apiMethodService.request<ParkingSubscriptionTableDto>(
      '/Backoffice/getParkingSubscriptionForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingSubscriptionForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  insertParkingFee(payload: SaveParkingFeePayload): Observable<ActionBackOfficeResponse> {
    return this.postCommand('/Backoffice/insertParkingFee', payload, 'insertParkingFee');
  }

  updateParkingFee(id: number, payload: SaveParkingFeePayload): Observable<ActionBackOfficeResponse> {
    return this.patchCommand(`/Backoffice/updateParkingFee/${id}`, payload, 'updateParkingFee');
  }

  insertParkingFeeGroup(payload: SaveParkingFeeGroupPayload): Observable<ActionBackOfficeResponse> {
    return this.postCommand('/Backoffice/insertParkingFeeGroup', payload, 'insertParkingFeeGroup');
  }

  updateParkingFeeGroup(id: number, payload: SaveParkingFeeGroupPayload): Observable<ActionBackOfficeResponse> {
    return this.patchCommand(`/Backoffice/updateParkingFeeGroup/${id}`, payload, 'updateParkingFeeGroup');
  }

  insertParkingSubscription(payload: SaveParkingSubscriptionPayload): Observable<ActionBackOfficeResponse> {
    return this.postCommand('/Backoffice/insertParkingSubscription', payload, 'insertParkingSubscription');
  }

  updateParkingSubscription(id: number, payload: SaveParkingSubscriptionPayload): Observable<ActionBackOfficeResponse> {
    return this.patchCommand(`/Backoffice/updateParkingSubscription/${id}`, payload, 'updateParkingSubscription');
  }

  insertMembershipPackage(payload: SaveParkingSubscriptionPayload): Observable<ActionBackOfficeResponse> {
    return this.postCommand('/Backoffice/insertMembershipPackage', payload, 'insertMembershipPackage');
  }

  updateMembershipPackage(id: number, payload: SaveParkingSubscriptionPayload): Observable<ActionBackOfficeResponse> {
    return this.patchCommand(`/Backoffice/updateMembershipPackage/${id}`, payload, 'updateMembershipPackage');
  }

  getMembershipPackages(siteId: number): Observable<MembershipPackageListRow[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<unknown[]>(
      '/Backoffice/GetMembershipPackages',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetMembershipPackages',
        token,
        headerOnly: false,
        params: { site_id: siteId },
      }
    ).pipe(
      map((rows) => (Array.isArray(rows) ? rows.map((r) => this.mapMembershipPackageRow(r)) : []))
    );
  }

  private mapMembershipPackageRow(row: unknown): MembershipPackageListRow {
    const raw = row as Record<string, unknown>;
    return {
      feeId: Number(raw['id'] ?? raw['Id'] ?? 0),
      name: String(raw['name'] ?? raw['Name'] ?? ''),
      figDay: Number(raw['fig_day'] ?? raw['Fig_day'] ?? raw['figDay'] ?? 0),
      fee: Number(raw['fee'] ?? raw['Fee'] ?? 0),
      vat: Boolean(raw['vat'] ?? raw['Vat'] ?? false),
      whTax: Number(raw['wh_tax'] ?? raw['Wh_tax'] ?? raw['whTax'] ?? 0),
      vehicleName: String(raw['vehicle_name'] ?? raw['Vehicle_name'] ?? raw['vehicleName'] ?? ''),
      subscriptionId: Number(raw['subscription_id'] ?? raw['Subscription_id'] ?? raw['subscriptionId'] ?? 0),
      vatIncluded: Boolean(raw['vat_included'] ?? raw['Vat_included'] ?? raw['vatIncluded'] ?? false),
      isOnline: Boolean(raw['is_online'] ?? raw['Is_online'] ?? raw['isOnline'] ?? false),
    };
  }

  getParkingFeeDropdown(searchText?: string | null): Observable<IdNameDto[]> {
    return this.getDropdown('/Backoffice/getParkingFeeDropdown', searchText);
  }

  getParkingFeeGroupDropdown(searchText?: string | null): Observable<IdNameDto[]> {
    return this.getDropdown('/Backoffice/getParkingFeeGroupDropdown', searchText);
  }

  getParkingGateGroupDropdown(siteId: number, searchText?: string | null): Observable<IdNameDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId };
    if (searchText?.trim()) params['searchText'] = searchText.trim();

    return this.apiMethodService.request<IdNameDto[]>(
      '/Backoffice/getParkingGateGroupDropdown',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingGateGroupDropdown',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getParkingRateBySite(siteId: number): Observable<ParkingRateOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingRateOption[]>(
      '/Backoffice/GetPakringRateBySite',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetPakringRateBySite',
        token,
        headerOnly: false,
        params: { siteId },
      }
    );
  }

  private getTable<T>(
    path: string,
    page: number,
    pageSize: number,
    searchText?: string | null
  ): Observable<T> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { page, pageSize };
    if (searchText?.trim()) params['searchText'] = searchText.trim();

    return this.apiMethodService.request<T>(path, 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: path,
      token,
      headerOnly: false,
      params,
    });
  }

  private getDropdown(path: string, searchText?: string | null): Observable<IdNameDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string> = {};
    if (searchText?.trim()) params['searchText'] = searchText.trim();

    return this.apiMethodService.request<IdNameDto[]>(path, 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: path,
      token,
      headerOnly: false,
      params,
    });
  }

  private postCommand(path: string, payload: unknown, label: string): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(path, 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: label,
      token,
      headerOnly: false,
      body: payload,
    });
  }

  private patchCommand(path: string, payload: unknown, label: string): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(path, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: label,
      token,
      headerOnly: false,
      body: payload,
    });
  }
}
